import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useLocalStore from 'src/store/useLocalStore';

import { Api } from 'src/models/Api.model';
import { CriteriaOperatorEnum } from 'src/models/GalenData/Criteria.model';
import { OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

const DEFAULT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export default function useGetGradingReceivedCount(
  refreshIntervalMs: number = DEFAULT_REFRESH_INTERVAL_MS,
) {
  const {
    selectedSiteId,
    setGradingReceivedCount,
    gradingReceivedCountResetToken,
  } = useLocalStore();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const intervalRef = useRef<number | null>(null);

  const fetchGradingReceivedCount = useCallback(async () => {
    try {
      const deviceCriteria = [
        {
          key: 'Status',
          value: OrderStatusEnum.REVIEW,
          operator: CriteriaOperatorEnum.Equal,
        },
        ...(selectedSiteId
          ? [
              {
                key: 'SiteId',
                value: selectedSiteId,
                operator: CriteriaOperatorEnum.Equal,
              },
            ]
          : []),
      ];

      const response = await Api.remote._count_device_data({
        deviceDataModelId: import.meta.env.VITE_EXAM_ORDER_MODEL_ID,
        deviceCriteria,
      });

      setGradingReceivedCount(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    }
  }, [
    handleHttpRequestError,
    language,
    selectedSiteId,
    setGradingReceivedCount,
    t,
  ]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      return;
    }

    fetchGradingReceivedCount();
    intervalRef.current = window.setInterval(
      fetchGradingReceivedCount,
      refreshIntervalMs,
    );
  }, [fetchGradingReceivedCount, refreshIntervalMs]);

  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, [
    gradingReceivedCountResetToken,
    selectedSiteId,
    startPolling,
    stopPolling,
  ]);
}
